import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import cx from 'classnames';

import {
  CryptoLabelPill,
  LangSwitcher,
  Loader,
  RiskIndicatorLimited,
  SvgIcon,
  Button,
} from '@/components';
import { getIconByNetwork, setWidgetCookie, getWidgetCookie } from '@/helpers';
import { useApiCall } from '@/hooks';

import amlbotLogo from '@/assets/images/logo-amlbot.svg';
import bgImg from '@/assets/images/ref-signup/bg.svg';
import filecheckImg from '@/assets/icons/filepcheck.svg';

import { LandingFooter } from '@/components/LandingFooter/LandingFooter';
import { ChainsEnum, ChainsDictionary, type WidgetUser } from '@/config';
import {
  AmlPreviewData,
  amlbotAPI,
  AmlPreviewResponse,
  AmlPreviewRequest,
  AmlFlowEnum,
  BaseRequest,
  WidgetUserResponse,
} from '@/api';
import { SignUpForm } from '../SignUp/components/SignUpForm/SignUpForm';
import { AddressConnections } from './components';
import { userSelectors } from '@/redux/selectors';
import { useAppSelector } from '@/redux/hooks';
import { useRefSignUpPartner } from './hooks/useRefSignUpPartner';
import { useAmlBotWidget } from './hooks/useAmlBotWidget';

import styles from './RefSignUp.module.sass';

export const RefSignUp = () => {
  const widget = useAmlBotWidget();
  const isAuth = useAppSelector(userSelectors.getAuth);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { logo: partnerLogo, label: partnerLabel } = useRefSignUpPartner();
  const [amlPreviewData, setAmlPreviewData] = useState<AmlPreviewData>();
  const [amlPreviewDataFetched, setAmlPreviewDataFetched] = useState(false);
  const [widgetUser, setWidgetUser] = useState<WidgetUser | null>(null);
  const [userFetched, setUserFetched] = useState(false);
  const [widgetVisible, setWidgetVisible] = useState(false);

  const [getAmlPreviewData, amlLoading, amlError] = useApiCall(async () => {
    const payload: AmlPreviewRequest = {
      ref: searchParams.get('ref') || '',
      uid: searchParams.get('hash') || '',
    };
    try {
      const response: AmlPreviewResponse =
        await amlbotAPI.fetchAmlPreview(payload);

      if (response.success) {
        const data: AmlPreviewData = {
          uid: response.data.uid,
          address: response.data.hash,
          network: response.data.network.toUpperCase() as ChainsEnum,
          riskScoreLevel: response.data.riskScoreLevel,
          blocklisted: response.data.blocklisted,
          exposures: response.data.exposures,
        };

        setAmlPreviewData(data);
      }
    } finally {
      setAmlPreviewDataFetched(() => true);
    }
  });

  const [registerWidgetUser] = useApiCall(async () => {
    const cachedUser = getWidgetCookie();

    if (cachedUser) {
      setWidgetUser(() => cachedUser);
      widget.init(cachedUser);
      setUserFetched(() => true);
      return;
    }

    const payload: BaseRequest = {};
    const response: WidgetUserResponse =
      await amlbotAPI.registerWidgetUser(payload);

    if (response.success) {
      const data: WidgetUser = {
        username: response.data.username,
        externalId: response.data.uid,
      };

      setWidgetUser(() => data);
      setWidgetCookie(data);
      widget.init(data);
    }

    setUserFetched(() => true);
  });

  const hasInfo = !!amlPreviewData?.uid;
  const showWidget = amlPreviewData && widget.isReady() && widgetUser;
  const amlFetchFailed = amlPreviewDataFetched && !amlPreviewData;
  const showFallback = !showWidget && (!widget.isLoading || amlFetchFailed);

  useEffect(() => {
    if (amlPreviewData || amlPreviewDataFetched) return;

    getAmlPreviewData();
  }, [amlPreviewData, amlPreviewDataFetched]);

  useEffect(() => {
    if (amlPreviewData && amlPreviewDataFetched && widget && !userFetched) {
      registerWidgetUser();
    }
  }, [amlPreviewData, amlPreviewDataFetched, widget, userFetched]);

  const sendToWidget = () => {
    if (amlPreviewData) {
      setWidgetVisible(true);
      widget.sendData({
        hash: amlPreviewData.address,
        network: amlPreviewData.network,
      });
    }
  };

  const classes = cx(styles.refSignUp, {
    [styles['has-info']]: hasInfo,
  });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  if (amlLoading) {
    return <Loader />;
  }

  return (
    <div
      className={classes}
      style={
        {
          '--bg-image': `url(${bgImg})`,
        } as CSSProperties
      }
    >
      <header className={styles.header}>
        <Link to="/" className={styles.logo}>
          <img src={amlbotLogo} alt="AMLBot" />
        </Link>

        <div className={styles.actions}>
          <Link to="/signin" className={styles.signInLink}>
            {t('translation.sign_in')}
          </Link>

          {partnerLogo && (
            <div className={styles.partner}>
              {t('translation.partnered_with')}{' '}
              <img src={partnerLogo} alt={partnerLabel || ''} />
            </div>
          )}

          <LangSwitcher
            fullLabel={false}
            className={styles.langSwitcher}
            isRounded
          />
        </div>
      </header>

      <div
        className={`${styles.content} ${widgetVisible ? styles.shrinked : ''}`}
      >
        <div
          style={{ display: widgetVisible ? 'block' : 'none' }}
          id="amlbot-inline-container"
          className={styles.inlineWidget}
        />

        {!widgetVisible && (
          <>
            {hasInfo && (
              <div className={styles.info}>
                <div className={styles.title}>
                  {t('translation.refSignUp.amlRiskResult')}
                </div>

                <CryptoLabelPill
                  iconUrl={getIconByNetwork(amlPreviewData?.network)}
                  label={ChainsDictionary[amlPreviewData?.network]?.name || ''}
                  protocol=""
                  abbr={ChainsDictionary[amlPreviewData?.network]?.symbol || ''}
                />

                <div className={styles.address}>
                  {amlPreviewData?.address || ''}
                </div>

                {!amlPreviewData?.blocklisted && (
                  <div className={styles.blacklistFree}>
                    <img src={filecheckImg} alt="" />
                    {t('translation.blacklist_free')}
                  </div>
                )}

                {amlPreviewData?.riskScoreLevel && (
                  <RiskIndicatorLimited
                    amlFlow={AmlFlowEnum.LITE}
                    value={amlPreviewData?.riskScoreLevel}
                  />
                )}

                {!!amlPreviewData?.exposures && (
                  <AddressConnections
                    low={amlPreviewData?.exposures?.low}
                    medium={amlPreviewData?.exposures?.medium}
                    high={amlPreviewData?.exposures?.high}
                  />
                )}
              </div>
            )}

            <div className={styles.signIn}>
              {showWidget && (
                <Button
                  size="xl"
                  corner="rect"
                  onClick={sendToWidget}
                  disabled={amlLoading}
                  fullwidth
                >
                  {t('translation.widget_check_btn')}
                </Button>
              )}

              {showFallback && (
                <>
                  <div className={styles.lock}>
                    <SvgIcon name="lock" />
                  </div>

                  <div className={styles.heading}>
                    <div className={styles.title}>
                      {t('translation.refSignUp.toContinue')}
                    </div>

                    {!hasInfo && (
                      <div className={styles.freeCheck}>
                        {t('translation.refSignUp.signUpAndGet')}
                        <span className={styles.badge}>
                          <SvgIcon name="aml" /> 1
                        </span>
                        {t('translation.refSignUp.freeCheck')}
                      </div>
                    )}

                    <div className={styles.description}>
                      {t('translation.already_have_one')}{' '}
                      <Link to="/signin" className={styles.link}>
                        {t('translation.sign_in')}
                      </Link>
                    </div>
                  </div>

                  {(widget.loadFailed || amlFetchFailed) && (
                    <SignUpForm
                      amlPreviewData={amlPreviewData}
                      hidePasswordConfirmation
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <LandingFooter type="white" />
    </div>
  );
};

export default RefSignUp;
